/**
 * Smart Shikka AI Core — PR98
 * Tool Request Queue Registry — Approval Required / No Tool Execution
 */
function deepFreeze(obj) {
    const propNames = Object.getOwnPropertyNames(obj);
    for (const name of propNames) {
        const value = obj[name];
        if (value && typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return Object.freeze(obj);
}
const BLOCKED_ACTIONS = [
    "agent_execution",
    "child_process",
    "network_call",
    "old_assistant_alias",
    "permission_mutation",
    "role_mutation",
    "runtime_unlock",
    "secret_literal",
    "shell_command",
    "tool_execution",
    "unapproved_db_write",
    "unapproved_file_write",
    "unapproved_network_call",
    "unknown_provider_call",
    "unknown_tool_execution",
    "unredacted_secret",
    "unsafe_env_dump",
    "workflow_execution",
    "browser_action",
    "desktop_action",
    "file_read",
    "file_write",
    "gate_unlock",
    "broad_runtime_unlock",
    "unscoped_approval",
    "approval_without_audit",
    "expired_approval_use",
    "tool_execution_without_queue",
    "agent_execution_without_queue",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    providerCallAllowedInPr98: false,
    secretLiteralAllowedInPr98: false,
    secretReadAllowedInPr98: false,
    runtimeUnlockAllowedInPr98: false,
    gateUnlockAllowedInPr98: false,
    agentExecutionAllowedInPr98: false,
    toolExecutionAllowedInPr98: false,
    workflowExecutionAllowedInPr98: false,
    documentRenderAllowedInPr98: false,
    iconInjectionAllowedInPr98: false,
    browserAutomationAllowedInPr98: false,
    desktopAutomationAllowedInPr98: false,
    fileMutationAllowedInPr98: false,
    fileReadAllowedInPr98: false,
    dbWriteAllowedInPr98: false,
    shellCommandAllowedInPr98: false,
    childProcessAllowedInPr98: false,
    roleMutationAllowedInPr98: false,
    permissionMutationAllowedInPr98: false,
    broadRuntimeUnlockAllowedInPr98: false,
    unscopedApprovalAllowedInPr98: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "tool_request_queue_summary",
        displayName: "Tool Request Queue Summary",
        description: "Summary of tool request queue with approval requirements. No tool execution — queue only.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Tool execution not yet enabled", "Queue only — no execution in PR98", "Approval required before any tool runs"],
        futurePrScope: ["Safe tool execution with approval", "Agent execution with approval"],
        notes: ["Queue stores sanitized request metadata only", "No tool runs until PR99+", "All requests require dual approval"],
    },
    {
        id: "deterministic_tool_request_contract",
        displayName: "Deterministic Tool Request Contract",
        description: "Contract for deterministic tool request creation. Request metadata is sanitized and stored.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Non-deterministic request rejected", "Request metadata must be sanitized", "No raw user data in request"],
        futurePrScope: ["Deterministic tool execution contract"],
        notes: ["Request creation is deterministic", "No randomness in queue", "Metadata sanitized before storage"],
    },
    {
        id: "tool_request_approval_required_gate",
        displayName: "Tool Request Approval Required Gate",
        description: "Gate requiring dual approval before any tool request can proceed. No bypass allowed.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_dual_approval_required_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Owner approval missing", "User approval missing", "Dual approval required"],
        futurePrScope: ["Conditional approval rules", "Delegated approval"],
        notes: ["Both owner and user must approve", "No approval bypass", "Gate cannot be unlocked without dual approval"],
    },
    {
        id: "tool_request_pending_state",
        displayName: "Tool Request Pending State",
        description: "State representation for a pending tool request awaiting approval.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyApprovalStatePreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_pending_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Request still pending", "Cannot proceed without approval", "Pending state is readonly"],
        futurePrScope: ["Pending state timeout", "Pending state notification"],
        notes: ["Pending requests have no runtime effect", "State is purely informational", "No side effects from pending state"],
    },
    {
        id: "tool_request_denied_state",
        displayName: "Tool Request Denied State",
        description: "State representation for a denied tool request. Denial is permanent for that request.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyApprovalStatePreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_denied_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Request permanently denied", "No retry on same request", "New request required after denial"],
        futurePrScope: ["Denial appeal process", "Denial audit export"],
        notes: ["Denied requests cannot be retried", "Denial is final", "Audit log records denial reason"],
    },
    {
        id: "tool_request_expired_state",
        displayName: "Tool Request Expired State",
        description: "State representation for an expired tool request. Expired requests cannot be used.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyApprovalStatePreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_pending_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Request expired", "expired_approval_use is blocked", "New request required after expiry"],
        futurePrScope: ["Expiry window configuration", "Expiry notification"],
        notes: ["Expired requests are permanently invalidated", "No reuse of expired approvals", "Expiry is time-bounded"],
    },
    {
        id: "tool_request_audit_contract",
        displayName: "Tool Request Audit Contract",
        description: "Audit contract for all tool request queue operations. Every action produces audit evidence.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["audit_report_request"],
        relatedUiPreviewIds: ["audit_report_route_card"],
        relatedChatPreviewIds: ["audit_transcript_preview"],
        relatedConversationStatePreviewIds: ["readonly_audit_transcript_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_audit_evidence_placeholder"],
        relatedActionRequestPreviewIds: ["readonly_audit_action_request_placeholder"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Audit evidence incomplete", "No action without audit", "approval_without_audit is blocked"],
        futurePrScope: ["Audit export", "Compliance reporting"],
        notes: ["Every queue operation generates audit evidence", "Audit is append-only", "No audit deletion"],
    },
    {
        id: "tool_execution_still_blocked",
        displayName: "Tool Execution Still Blocked",
        description: "Explicit declaration that tool execution remains blocked in PR98. Queue only.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["tool_execution_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_tool_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Tool execution disabled in PR98", "Queue only — no execution", "Execution requires PR99+"],
        futurePrScope: ["Safe tool execution gate (PR99+)"],
        notes: ["No tool executes in PR98", "Queue stores metadata only", "Execution path does not exist yet"],
    },
    {
        id: "agent_execution_still_blocked",
        displayName: "Agent Execution Still Blocked",
        description: "Explicit declaration that agent execution remains blocked in PR98. No agents run.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["future_agent_task_request"],
        relatedUiPreviewIds: ["future_agent_task_blocked_card"],
        relatedChatPreviewIds: ["tool_execution_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_tool_execution_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_agent_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Agent execution disabled in PR98", "No agents run", "Agent requires separate approval gate"],
        futurePrScope: ["Agent execution approval gate", "Multi-step agent approval"],
        notes: ["No agent executes in PR98", "Agent execution is a separate future concern", "Blocked by design"],
    },
    {
        id: "future_safe_tool_execution_ready",
        displayName: "Future Safe Tool Execution Ready",
        description: "Placeholder for future safe tool execution capability. Not active in PR98.",
        sourceModules: ["smartShikkaAssistantToolRequestQueueRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["future_chat_runtime_blocked_card"],
        relatedConversationStatePreviewIds: ["future_persistent_conversation_state_blocked"],
        relatedApprovalStatePreviewIds: ["future_approval_runtime_blocked_state"],
        relatedActionRequestPreviewIds: ["future_action_execution_runtime_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Future capability not yet available", "Requires PR99+ implementation", "All execution blocked until then"],
        futurePrScope: ["Safe sandboxed tool execution", "Result redaction", "Tool output audit"],
        notes: ["This entry is a future placeholder", "No execution capability exists yet", "Activation requires separate PR"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantToolRequestQueueEntries() {
    return registry;
}
export function getSmartShikkaAssistantToolRequestQueueEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantToolRequestQueueRegistry.js.map